import axios from 'axios';

export type SignatoryPerson = {
  name: string;
  functions: string[];
  roleText: string;
};

export type SignatoryStatementSigner = {
  name: string;
  functions: string[];
  roleText: string;
};

/** One line of the registered firmateckning wording; `signer` marks a named person/role entry. */
export type SignatoryStatementLine = {
  text: string;
  indent: 0 | 1;
  signer?: SignatoryStatementSigner;
};

/** One statement of the registered wording, with the people it resolves to via registry roles. */
export type SignatoryStatement = {
  lines: SignatoryStatementLine[];
  signers: SignatoryStatementSigner[];
};

export type SignatoryMatch = {
  signeeName: string;
  registryName: string;
  roleText: string;
  isCeo: boolean;
};

/**
 * `ok`       - the selected signees satisfy the registered rule (or one of them is CEO)
 * `mismatch` - they do not, or a signee is not registered at the company at all
 * `unknown`  - no machine-readable rule (CreditSafe source, or wording the parser can't read)
 * `none`     - no signees selected yet
 */
export type SigningStatus = 'ok' | 'mismatch' | 'unknown' | 'none';

export type SignatoryCheck =
  | {
      status: 'ok';
      source: 'syna' | 'creditsafe';
      people: SignatoryPerson[];
      statements: SignatoryStatement[];
      signing: {
        status: SigningStatus;
        viaCeo: boolean;
        matched: SignatoryMatch[];
        unmatched: string[];
      };
    }
  | { status: 'no_credit_service' | 'not_found'; source: null };

/**
 * Registered signatories for a company, plus an advisory check of the proposed signees.
 * Purely informational — callers must never gate submission on the result.
 */
export const checkSignatories = async (
  organizationNumber: string,
  signeeNames: string[],
  signal?: AbortSignal,
): Promise<SignatoryCheck> => {
  const response = await axios.post<SignatoryCheck>(
    '/api/contract/signatory-check',
    { organizationNumber, signeeNames },
    { signal },
  );
  return response.data;
};

/** True when `error` is the lookup's own request being aborted — a superseded call, not a failure. */
export const isSignatoryLookupAborted = (error: unknown): boolean => axios.isCancel(error);
